import React from 'react';
import PageLayout from '../components/PageLayout';
import SEOHead from '../components/SEOHead';

const measures = [
  { icon: '/pictures/firewalls_21293cd107.svg', title: 'Firewalls', desc: 'Firewalls are one of the most fundamental network security appliances that control incoming and outgoing traffic on networks. We provide a baseline of security for your network.' },
  { icon: '/pictures/shield_check_7246d46e1b.svg', title: 'Antivirus and malware software', desc: 'Designed to detect and remove viruses, spyware and other types of malware from your computer and network infrastructure.' },
  { icon: '/pictures/intrusion_detection_systems_82cce9bc0b.svg', title: 'Intrusion detection systems', desc: 'IDS is used to detect anomalies with the goal of catching hackers before they do any real damage to your network. IDS can be either network-based or host-based.' },
  { icon: '/pictures/virtual_private_networks_87d39ece1f.svg', title: 'Virtual private networks (VPNs)', desc: 'Provide a network host for your team and resources that are private and secured behind 2FA and data encryption, virtually accessible so your team can connect from anywhere.' },
  { icon: '/pictures/access_controls_4aabd4e0ee.svg', title: 'Access controls', desc: 'Provides access to remote users or locations via VPN. Authorized remote users can access resources without exposing those resources to unauthorized parties.' },
  { icon: '/pictures/email_security_601ebfc2ed.svg', title: 'Email security', desc: 'One misguided click is enough to cause a company-wide security crisis. Email security tools avoid phishing and filter emails in the background to help identify suspicious emails.' },
];

const howItWorks = [
  'Network security revolves around 2 processes: authentication and authorization.',
  'Authentication refers to the process of verifying the identity of someone or something that is trying to access a particular resource or system.',
  'Authorization can be based on various factors, such as job role, security clearance or specific permissions granted.',
];

export default function NetworkSecurity() {
  return (
    <PageLayout>
      <SEOHead
        title="Network Security Solutions"
        description="Comprehensive network security solutions including firewalls, VPNs, intrusion detection systems, access controls, and email security to protect your organization."
        keywords="network security, firewall, VPN, intrusion detection, IDS, access control, email security, network protection"
        url="/services/network-security"
      />
      {/* Page Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Services</span>
            <span style={{ color: '#D1D5DB' }}>/</span>
            <span style={{ fontSize: '14px', color: '#0099CC', fontWeight: '500' }}>Network Security</span>
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            Network Security
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '640px', lineHeight: 1.7 }}>
            Find the best network security software for your company or organization with DISION Tech today.
          </p>
        </div>
      </div>

      {/* What is Network Security */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '24px', letterSpacing: '-0.5px' }}>What is Network Security?</h2>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8 }}>
              Network security is the practice of implementing measures and protocols to protect a computer network from unauthorized access, misuse, modification, or denial of service (DoS) attacks. With the rise of cybercrime and the increasing dependence on technology in all aspects of life, network security has become more important than ever.
            </p>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/net_Sec_ed720c1d33.png" alt="Network Security" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ padding: '80px 24px', background: '#F9FAFB' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/how_it_works_73f6b0e32b.png" alt="How Network Security Works" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&q=80'; }} />
          </div>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '32px', letterSpacing: '-0.5px' }}>How does Network Security work?</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {howItWorks.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '14px', padding: '16px 20px', borderRadius: '12px', background: 'white', border: '1px solid #E5E7EB', alignItems: 'flex-start' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#0099CC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <span style={{ fontSize: '15px', color: '#374151', lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Network security measures */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Network security measures</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '560px', lineHeight: 1.7 }}>
            We aim to prevent unauthorized access, detect any intrusions that may occur and respond quickly and effectively to any security breaches.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {measures.map((m, i) => (
              <div key={i} className="service-card" style={{ background: '#F9FAFB', borderRadius: '16px', padding: '28px', border: '1px solid #E5E7EB' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', padding: '12px' }}>
                  <img src={m.icon} alt={m.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => e.target.style.display='none'} />
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#111827', marginBottom: '10px' }}>{m.title}</h3>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
